/* eslint-disable @typescript-eslint/no-unused-vars */
import { convertDateTimeKeepTimezone } from "@/common/datetime/TimeFunction";
import { IMeetingInfo, ITimeInfo, TimeInfo } from "@/common/entity/Entity";
import { EColorPermission, EColorPermissionFloorPlan } from "@/common/entity/Enum";
import { AppointmentBusyStatus } from "@/common/enums";
import { EventStatusEnum, ITimeLineEvent } from "@/components/ui/TimeLine/Models";
import { EDeclineType } from "./Enums";
import { IResourceFloorPlanInfo } from "./FloorplanEntity";
import { UidGenerator } from "@/shared/utilities/UidGenerator";
// import { DateTimePeriod } from "@/services/organization-service/entity/OutLookEntity";

export interface IExchangeProperty {
  bookInPolicies?: string[];
  bookingWindowInDay?: number;
  conflictPercentageAllowed?: number;
  isAllBookInPolicy?: boolean;
  isAllRequestInPolicy?: boolean;
  isAllRequestOutOfPolicy?: boolean;
  isAllowConflict?: boolean;
  isAllowRecurring?: boolean;
  isEnforceSchedulingHorizon?: boolean;
  isForwardRequestsToDelegates?: boolean;
  isScheduleOnlyDuringWorkHours?: boolean;
  isTentativePendingApproval?: boolean;
  maximumConflictInstance?: number;
  maximumDurationInMinutes?: number;
  minimumDurationInMinutes?: number;
  requestInPolices?: string[];
  requestOutOfPolicies?: string[];
}

export class ExchangeProperty {
  readonly bookInPolicies: string[];
  readonly bookingWindowInDay: number;
  readonly conflictPercentageAllowed: number;
  readonly isAllBookInPolicy: boolean;
  readonly isAllRequestInPolicy: boolean;
  readonly isAllRequestOutOfPolicy: boolean;
  readonly isAllowConflict: boolean;
  readonly isAllowRecurring: boolean;
  readonly isEnforceSchedulingHorizon: boolean;
  readonly isForwardRequestsToDelegates: boolean;
  readonly isScheduleOnlyDuringWorkHours: boolean;
  readonly isTentativePendingApproval: boolean;
  readonly maximumConflictInstance: number;
  readonly maximumDurationInMinutes: number;
  readonly minimumDurationInMinutes: number;
  readonly requestInPolices: string[];
  readonly requestOutOfPolicies: string[];

  constructor(data?: IExchangeProperty) {
    this.bookInPolicies = data?.bookInPolicies || [];
    this.bookingWindowInDay = data?.bookingWindowInDay || 0;
    this.conflictPercentageAllowed = data?.conflictPercentageAllowed || 0;
    this.isAllBookInPolicy = !!data?.isAllBookInPolicy;
    this.isAllRequestInPolicy = !!data?.isAllRequestInPolicy;
    this.isAllRequestOutOfPolicy = !!data?.isAllRequestOutOfPolicy;
    this.isAllowConflict = !!data?.isAllowConflict;
    this.isAllowRecurring = !!data?.isAllowRecurring;
    this.isEnforceSchedulingHorizon = !!data?.isEnforceSchedulingHorizon;
    this.isForwardRequestsToDelegates = !!data?.isForwardRequestsToDelegates;
    this.isScheduleOnlyDuringWorkHours = !!data?.isScheduleOnlyDuringWorkHours;
    this.isTentativePendingApproval = !!data?.isTentativePendingApproval;
    this.maximumConflictInstance = data?.maximumConflictInstance || 0;
    this.maximumDurationInMinutes = data?.maximumDurationInMinutes || 0;
    this.minimumDurationInMinutes = data?.minimumDurationInMinutes || 0;
    this.requestInPolices = data?.requestInPolices || [];
    this.requestOutOfPolicies = data?.requestOutOfPolicies || [];
  }

  toJson(): IExchangeProperty {
    return JSON.parse(JSON.stringify(this));
  }
}

export interface IExtensionProperty {
  alias?: string;
  capacity?: number;
  companyName?: string;
  division?: string;
  mailboxSetting_TimeFormat?: string;
  mailboxSetting_TimeZone?: string;
  onPremisesDistinguishedName?: string;
  physicalDeliverOfficeName?: string;
  primarySMTPAddress?: string;
  resourceCustom?: string[];
  resourceTypeValue?: string;
  tags?: string[];
  workingHours_DaysOfWeek?: string;
  workingHours_EndTime?: string;
  workingHours_StartTime?: string;
  workingHours_TimeZone?: string;
}

export class ExtensionProperty {
  readonly alias: string;
  readonly capacity: number;
  readonly companyName: string;
  readonly division: string;
  readonly mailboxSetting_TimeFormat: string;
  readonly mailboxSetting_TimeZone: string;
  readonly onPremisesDistinguishedName: string;
  readonly physicalDeliverOfficeName: string;
  readonly primarySMTPAddress: string;
  readonly resourceCustom: string[];
  readonly resourceTypeValue: string;
  readonly tags: string[];
  readonly workingHours_DaysOfWeek: string;
  readonly workingHours_EndTime: string;
  readonly workingHours_StartTime: string;
  readonly workingHours_TimeZone: string;

  constructor(data?: IExtensionProperty) {
    this.alias = data?.alias || "";
    this.capacity = data?.capacity || 0;
    this.companyName = data?.companyName || "";
    this.division = data?.division || "";
    this.mailboxSetting_TimeFormat = data?.mailboxSetting_TimeFormat || "";
    this.mailboxSetting_TimeZone = data?.mailboxSetting_TimeZone || "";
    this.onPremisesDistinguishedName = data?.onPremisesDistinguishedName || "";
    this.physicalDeliverOfficeName = data?.physicalDeliverOfficeName || "";
    this.primarySMTPAddress = data?.primarySMTPAddress || "";
    this.resourceCustom = data?.resourceCustom || [];
    this.resourceTypeValue = data?.resourceTypeValue || "";
    this.tags = data?.tags || [];
    this.workingHours_DaysOfWeek = data?.workingHours_DaysOfWeek || "";
    this.workingHours_EndTime = data?.workingHours_EndTime || "";
    this.workingHours_StartTime = data?.workingHours_StartTime || "";
    this.workingHours_TimeZone = data?.workingHours_TimeZone || "";
  }

  toJson(): IExtensionProperty {
    return JSON.parse(JSON.stringify(this));
  }
}

export interface IResourceCategoryInfo {
  audioDeviceName?: string | null;
  displayDeviceName?: string | null;
  isWheelChairAccessible?: boolean;
  videoDeviceName?: string | null;
}

export class ResourceCategoryInfo {
  readonly audioDeviceName: string | null;
  readonly displayDeviceName: string | null;
  readonly isWheelChairAccessible: boolean;
  readonly videoDeviceName: string | null;

  constructor(data?: IResourceCategoryInfo) {
    this.audioDeviceName = data?.audioDeviceName || null;
    this.displayDeviceName = data?.displayDeviceName || null;
    this.isWheelChairAccessible = !!data?.isWheelChairAccessible;
    this.videoDeviceName = data?.videoDeviceName || null;
  }

  toJson(): IResourceCategoryInfo {
    return JSON.parse(JSON.stringify(this));
  }
}

export interface IResourceLocationInfo {
  name?: string;
  parentId?: string;
  phone?: string;
  sequenceNumber?: number;
  timeZone?: string;
}

export class ResourceLocationInfo {
  readonly name: string;
  readonly parentId: string;
  readonly phone: string;
  readonly sequenceNumber: number;
  readonly timeZone: string;

  constructor(data?: IResourceLocationInfo) {
    this.name = data?.name || "";
    this.parentId = data?.parentId || "";
    this.phone = data?.phone || "";
    this.sequenceNumber = data?.sequenceNumber || 0;
    this.timeZone = data?.timeZone || "";
  }

  toJson(): IResourceLocationInfo {
    return JSON.parse(JSON.stringify(this));
  }
}

export interface IResourcePermission {
  resourceId?: string;
  isNotExistsExchangeProperty: boolean;
  meetingAcceptsToBusy: IResourceMeetingInfo[]; //status busy
  meetingAcceptsToTentative: IResourceMeetingInfo[]; //status tentative
  meetingDeclines: IResourceMeetingInfo[]; //stutus decline
  appointments?: IAppointmentInfo[];
}

export class ResourcePermission {
  resourceId?: string;
  isNotExistsExchangeProperty: boolean;
  meetingAcceptsToBusy: ResourceMeetingInfo[]; //status busy
  meetingAcceptsToTentative: ResourceMeetingInfo[]; //status tentative
  meetingDeclines: ResourceMeetingInfo[]; //stutus decline
  appointments: AppointmentInfo[];

  constructor(data?: IResourcePermission, isKeepValueDate?: boolean) {
    this.isNotExistsExchangeProperty = !!data?.isNotExistsExchangeProperty;
    this.meetingAcceptsToBusy = data?.meetingAcceptsToBusy?.length ? data.meetingAcceptsToBusy.map((item: IResourceMeetingInfo) => new ResourceMeetingInfo(item)) : [];
    this.meetingAcceptsToTentative = data?.meetingAcceptsToTentative?.length ? data?.meetingAcceptsToTentative.map((item: IResourceMeetingInfo) => new ResourceMeetingInfo(item)) : [];
    this.meetingDeclines = data?.meetingDeclines?.length ? data.meetingDeclines.map((item: IResourceMeetingInfo) => new ResourceMeetingInfo(item)) : [];
    this.appointments = (data?.appointments || []).map((item: IAppointmentInfo) => new AppointmentInfo(item, isKeepValueDate));
  }

  get isAcceptSelect(): boolean {
    if (this.meetingDeclines.length) {
      return false;
    }
    return true;
  }

  get colorPermission(): string {
    if (this.meetingDeclines.length) {
      return EColorPermissionFloorPlan.Disable;
    }
    return EColorPermissionFloorPlan.Available;
  }

  get showAs(): AppointmentBusyStatus {
    if (this.meetingAcceptsToTentative?.length) {
      return AppointmentBusyStatus.Tentative;
    }
    return AppointmentBusyStatus.Busy;
  }

  get isTentative(): boolean {
    return !!this.meetingAcceptsToTentative.length;
  }

  isAcceptSelectRecurring(quantityMeetingInfos: number): boolean {
    if (this.meetingDeclines.length && this.meetingDeclines.length === quantityMeetingInfos) {
      return false;
    }
    return true;
  }

  colorPermissionRecurring(quantityMeetingInfos: number): string {
    if (this.meetingDeclines.length && this.meetingDeclines.length === quantityMeetingInfos) {
      return EColorPermissionFloorPlan.Disable;
    }
    return EColorPermissionFloorPlan.Available;
  }

  hasIconPermission(isRecurring?: boolean, quantityMeetingInfos?: number): boolean {
    if (isRecurring) {
      if (this.meetingDeclines.length && this.meetingDeclines.length === quantityMeetingInfos) {
        return true;
      }
      return false;
    } else {
      return !!this.meetingDeclines.length;
    }
  }

  toJson(): IResourcePermission {
    return JSON.parse(JSON.stringify(this));
  }
}

export interface IResourceMeetingInfo {
  startTime?: string;
  endTime?: string;
  declineType?: EDeclineType | null;
}

export class ResourceMeetingInfo {
  startTime: string;
  endTime: string;
  declineType?: EDeclineType | null;

  constructor(data?: IResourceMeetingInfo) {
    this.startTime = data?.startTime || "";
    this.endTime = data?.endTime || "";
    this.declineType = data?.declineType || null;
  }

  toJson(): IResourceMeetingInfo {
    return JSON.parse(JSON.stringify(this));
  }
}
export interface IOrderInfo {
  iCalUID: string;
  resourceAdId: string;
  guid: string;
  hasOrder: boolean;
  haveNormalOrder: boolean;
}

export interface IResourceInfo {
  adId?: string;
  guid?: string;
  uuid?: string;
  email?: string;
  name?: string;
  displayName?: string;
  displayEmail?: string;
  description?: string;
  capacity?: number;
  categoryIds?: string[];
  groups?: string[];
  imageURLs?: string[];
  isDeleted?: boolean;
  locationId?: string;
  parentId?: string;
  phone?: string;
  sequenceNumber?: number;
  timeZone?: string;
  categoryInfo?: IResourceCategoryInfo;
  locationInfo?: IResourceLocationInfo;
  exchangeProperty?: IExchangeProperty;
  extensionProperty?: IExtensionProperty;
  permission?: IResourcePermission;
  locationDisplayName: string;
  resourceFloorPlanInfo?: IResourceFloorPlanInfo[] | null;
  isLightResource?: boolean;
  isWorkspace?: boolean;
  resourceType?: string;
  isRCRegistered?: boolean;
  orders?: IOrderInfo | null;
}

export class ResourceInfo {
  adId: string;
  guid: string;
  email: string;
  uuid: string;
  name: string;
  displayName: string;
  displayEmail: string;
  description: string;
  capacity: number;
  categoryIds: string[];
  groups: string[];
  imageURLs: string[];
  isDeleted: boolean;
  locationId: string;
  parentId: string;
  phone: string;
  sequenceNumber: number;
  timeZone: string;
  categoryInfo: ResourceCategoryInfo;
  locationInfo: ResourceLocationInfo;
  exchangeProperty: ExchangeProperty;
  extensionProperty: ExtensionProperty;
  permission: ResourcePermission;
  locationDisplayName: string;
  organizer?: IAppointmentOrganizer;
  resourceFloorPlanInfo?: IResourceFloorPlanInfo[] | null = null;
  isLightResource?: boolean;
  isWorkspace?: boolean;
  resourceType: string;
  isRCRegistered: boolean;
  orders?: IOrderInfo | null;

  constructor(data?: IResourceInfo, isKeepValueDate?: boolean) {
    this.adId = data?.adId || "";
    this.guid = data?.guid || "";
    this.uuid = data?.uuid || UidGenerator.NewUid();
    this.name = data?.name || "";
    this.email = data?.email?.toLowerCase() || "";
    this.displayEmail = data?.displayEmail?.toLowerCase() || "";
    this.displayName = data?.displayName || "";
    this.description = data?.description || "";
    this.capacity = data?.capacity || 0;
    this.categoryIds = data?.categoryIds || [];
    this.groups = data?.groups || [];
    this.imageURLs = data?.imageURLs || [];
    this.isDeleted = !!data?.isDeleted;
    this.locationId = data?.locationId || "";
    this.parentId = data?.parentId || "";
    this.phone = data?.phone || "";
    this.sequenceNumber = data?.sequenceNumber || 0;
    this.timeZone = data?.timeZone || "";
    this.categoryInfo = data?.categoryInfo ? new ResourceCategoryInfo(data.categoryInfo) : new ResourceCategoryInfo();
    this.locationInfo = data?.locationInfo ? new ResourceLocationInfo(data.locationInfo) : new ResourceLocationInfo();
    this.exchangeProperty = data?.exchangeProperty ? new ExchangeProperty(data.exchangeProperty) : new ExchangeProperty();
    this.extensionProperty = data?.extensionProperty ? new ExtensionProperty(data.extensionProperty) : new ExtensionProperty();
    this.permission = data?.permission ? new ResourcePermission(data.permission, isKeepValueDate) : new ResourcePermission();
    this.locationDisplayName = data?.locationDisplayName || "Locations";
    this.resourceFloorPlanInfo = data?.resourceFloorPlanInfo || null;
    this.isLightResource = data?.isLightResource ?? false;
    this.isWorkspace = data?.isWorkspace ?? false;
    this.resourceType = data?.resourceType || "";
    this.isRCRegistered = !!data?.isRCRegistered;
    this.orders = data?.orders || null;
  }

  get calculateBorder(): React.CSSProperties {
    const borderStyle: React.CSSProperties = {
      borderLeft: EColorPermission.DefaultBorder,
    };
    borderStyle.borderLeft = EColorPermission.Available;
    //#region Older logic color border left style color

    if (this.permission.meetingDeclines.length) {
      borderStyle.borderLeft = EColorPermission.PermissionAlert;
    }
    return borderStyle;
  }

  calculateBorderRecurring(quantityMeetingInfos: number): React.CSSProperties {
    const borderStyle: React.CSSProperties = {
      borderLeft: EColorPermission.DefaultBorder,
    };
    borderStyle.borderLeft = EColorPermission.Available;
    //#region Older logic color border left style color

    if (this.permission.meetingDeclines.length && this.permission.meetingDeclines.length === quantityMeetingInfos) {
      borderStyle.borderLeft = EColorPermission.PermissionAlert;
    }
    return borderStyle;
  }

  toJson(): IResourceInfo {
    return JSON.parse(JSON.stringify(this));
  }
}

export interface IIconPermission {
  declineType?: EDeclineType | null;
  desc?: string;
}

export interface IFetchResourceRequest {
  orgId: string;
  tenantId: string;
  requestBody: IResourceRequestBody;
}

export interface IResourceRequestBody {
  Organizer: string;
  MeetingInfos: IMeetingInfo[];
  IsRecurring: boolean;
  EmailGroups: string[];
  Filters: IFilterResourceRequest | null;
  IsExternal?: boolean;
  Guid?: string;
  SeriesMasterUId?: string | null;
}

export interface IFilterResource {
  name: string | null;
  categoryIds: string[] | null;
  locationIds: string[] | null;
  categoryCondition: string | null;
  indexLocation?: number;
  isAvailable?: boolean;
  resourceTypes?: string;
}

export interface IPaging {
  top: number;
  skip: number;
}

export interface IFilterResourceRequest {
  LocationIds?: string | null;
  CategoryIds?: string | null;
  NameContains?: string | null;
  Page?: number;
  PageSize?: number;
  Emails?: string | null;
  IsAvailable?: boolean;
  ResourceTypes?: string | null;
}

export interface IAppointmentOrganizer {
  emailAddress?: {
    displayName?: string;
    address?: string;
  };
}

export class AppointmentOrganizer {
  displayName: string;
  address: string;

  constructor(data?: IAppointmentOrganizer) {
    this.displayName = data?.emailAddress?.displayName || "";
    this.address = data?.emailAddress?.address || "";
  }

  toJson(): IAppointmentOrganizer {
    return {
      emailAddress: {
        displayName: this.displayName,
        address: this.address,
      },
    };
  }
}

export interface IAppointmentInfo {
  guid?: string;
  subject?: string;
  endTime?: ITimeInfo;
  startTime?: ITimeInfo;
  preBufferTime?: number;
  postBufferTime?: number;
  organizer?: IAppointmentOrganizer;
  status?: string;
}

export class AppointmentInfo {
  guid: string;
  subject: string;
  endTime: TimeInfo;
  startTime: TimeInfo;
  preBufferTime: number = 0;
  postBufferTime: number = 0;
  organizer: AppointmentOrganizer;
  status?: string;

  constructor(data?: IAppointmentInfo, isKeepValueDate?: boolean) {
    if (!isKeepValueDate) {
      if (data?.startTime) {
        data.startTime.dateTime = convertDateTimeKeepTimezone(data.startTime.dateTime as string);
      }
      if (data?.endTime) {
        data.endTime.dateTime = convertDateTimeKeepTimezone(data.endTime.dateTime as string);
      }
    }
    this.guid = data?.guid || "";
    this.subject = data?.subject || "";
    this.endTime = new TimeInfo(data?.endTime);
    this.startTime = new TimeInfo(data?.startTime);
    this.organizer = data?.organizer ? new AppointmentOrganizer(data?.organizer) : new AppointmentOrganizer();
    this.status = data?.status;
  }

  toJson(): IAppointmentInfo {
    return {
      guid: this.guid,
      subject: this.subject,
      endTime: this.endTime.toJson(),
      startTime: this.startTime.toJson(),
      preBufferTime: this.preBufferTime,
      postBufferTime: this.postBufferTime,
      organizer: this.organizer.toJson(),
    };
  }
}

export interface IAppointmentByTimeRangeRequest {
  CalendarIds: string[];
  StartDate: string;
  EndDate: string;
}

export interface IAppointmentByTimeRangeResponse {
  resourceId: string;
  appointments: IAppointmentInfo[];
}

export class AppointmentByTimeRangeInfo {
  resourceId: string;
  events: ITimeLineEvent[];

  constructor(data?: IAppointmentByTimeRangeResponse) {
    this.resourceId = data?.resourceId || "";
    this.events = (data?.appointments || []).map((slot) => {
      return {
        id: slot.guid,
        start: new Date(convertDateTimeKeepTimezone((slot.startTime?.dateTime as string) || "")),
        end: new Date(convertDateTimeKeepTimezone((slot.endTime?.dateTime as string) || "")),
        timelineStatus: EventStatusEnum.Busy,
        subject: slot.subject,
        postBufferTime: slot.postBufferTime,
        preBufferTime: slot.preBufferTime,
        workspaceInfo: undefined,
        organizerEmail: slot.organizer?.emailAddress?.address || "",
      };
    }) as ITimeLineEvent[];
  }
}
